import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BRAND } from "@/lib/brand";

/**
 * Legal — a single component that renders Privacy or Terms depending on `variant`.
 * Kept intentionally simple, mental-health-first, and legally cautious.
 */
export default function Legal({ variant = "privacy" }) {
    const isPrivacy = variant === "privacy";
    return (
        <div className="relative z-10 min-h-screen">
            <header className="px-6 md:px-12 py-6 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2.5" data-testid="legal-brand">
                    <img src={BRAND.logo} alt={BRAND.fullName} className="w-8 h-8 object-contain" />
                    <span className="font-heading text-2xl tracking-tight text-ink">{BRAND.handle}</span>
                </Link>
                <Link to="/" className="text-sm text-inkSoft hover:text-ink font-body inline-flex items-center gap-1.5" data-testid="legal-back">
                    <ArrowLeft className="w-4 h-4" /> home
                </Link>
            </header>

            <main className="max-w-3xl mx-auto px-6 md:px-12 py-8 pb-24">
                <h1 className="font-heading text-4xl md:text-5xl tracking-tight text-ink mb-3">
                    {isPrivacy ? "privacy" : "terms"}
                </h1>
                <p className="text-inkSoft font-body italic mb-10">last updated: February 2026</p>

                {isPrivacy ? <PrivacyBody /> : <TermsBody />}

                <div className="mt-16 bg-clay/60 border border-clayHover rounded-3xl p-6 text-sm text-inkSoft font-body">
                    questions or a data request? write to us at{" "}
                    <a href="mailto:hello@paxmind.ai" className="text-terracotta hover:underline">hello@paxmind.ai</a>.
                </div>

                <div className="mt-10 flex gap-6 text-sm text-inkSoft font-body">
                    <Link to="/privacy" className="hover:text-ink">privacy</Link>
                    <Link to="/terms" className="hover:text-ink">terms</Link>
                    <Link to="/" className="hover:text-ink">home</Link>
                </div>
            </main>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section className="mb-10 font-body text-ink leading-relaxed">
            <h2 className="font-heading text-xl tracking-tight text-ink mb-3">{title}</h2>
            <div className="space-y-3 text-[15px]">{children}</div>
        </section>
    );
}

function PrivacyBody() {
    return (
        <>
            <div className="bg-[#EAF2FB] border border-terracotta/30 rounded-3xl p-6 mb-10 font-body text-ink">
                <p className="font-medium mb-2">the short version</p>
                <p className="text-sm text-inkSoft leading-relaxed">
                    Pax stores what you say so it can remember you across sessions. Your conversations are private to your account.
                    We don't sell your data. You can delete your account and everything in it at any time.
                </p>
            </div>

            <Section title="what we collect">
                <p>· your name and email when you sign up (or Google profile if you sign in with Google).</p>
                <p>· the messages you send Pax and the replies Pax gives you.</p>
                <p>· any journal entries, mood ratings and memories you save.</p>
                <p>· basic technical info (browser, device, timestamps) for anti-abuse and diagnostics.</p>
            </Section>

            <Section title="what we do with it">
                <p>· we use your messages to power Pax's replies through third-party AI models (Anthropic, OpenAI, ElevenLabs). Your name and identifiers are not shared with those providers; only the content of the current conversation and a compact summary of prior context.</p>
                <p>· we generate short private "memories" (summaries) so Pax can be continuous across sessions. These are visible only to you.</p>
                <p>· we do not train any model on your conversations. we do not sell your data. we do not show ads.</p>
            </Section>

            <Section title="who sees your data">
                <p>· you.</p>
                <p>· our infrastructure providers (MongoDB, our hosting) — under standard data-processing agreements.</p>
                <p>· the AI providers listed above, only for the message they need to answer.</p>
                <p>· nobody else. no marketers, no data brokers, no employers.</p>
            </Section>

            <Section title="retention & deletion">
                <p>your data is kept while your account is active. you can delete individual memories or journals at any time. account deletion (email us or use the profile page) removes conversations, memories, journals and the account row within 30 days.</p>
            </Section>

            <Section title="crisis situations">
                <p>Pax is not a crisis service. if a message contains explicit self-harm intent, we may surface a crisis card with helplines. we do not proactively contact anyone on your behalf, but if a court order compels us we will comply with local law.</p>
            </Section>

            <Section title="children">
                <p>Pax is intended for people 16 and older. if you're younger, please use it with a trusted adult, or find services designed for your age group.</p>
            </Section>

            <Section title="your rights">
                <p>you can request a copy of your data, correct it, or delete it at any time. write to <a className="text-terracotta hover:underline" href="mailto:hello@paxmind.ai">hello@paxmind.ai</a>.</p>
            </Section>

            <Section title="changes">
                <p>if we change this policy in a meaningful way, we'll show a banner in-app before the change takes effect.</p>
            </Section>
        </>
    );
}

function TermsBody() {
    return (
        <>
            <div className="bg-[#EAF2FB] border border-terracotta/30 rounded-3xl p-6 mb-10 font-body text-ink">
                <p className="font-medium mb-2">the short version</p>
                <p className="text-sm text-inkSoft leading-relaxed">
                    Pax is a companion, not a therapist. Use it kindly. If you're in crisis, please talk to a real human.
                </p>
            </div>

            <Section title="what Pax is (and isn't)">
                <p>Pax is a conversational companion powered by AI. it is <span className="font-medium">not</span> a licensed therapist, physician, or mental-health professional. Pax cannot diagnose, treat, or provide medical, legal or financial advice.</p>
                <p>if you are in crisis or thinking about hurting yourself, please contact:</p>
                <ul className="pl-6 list-disc space-y-1 text-inkSoft">
                    <li>India — KIRAN: <span className="text-ink">1800-599-0019</span> · iCall: <span className="text-ink">+91 9152987821</span></li>
                    <li>United States — 988 Suicide & Crisis Lifeline</li>
                    <li>Elsewhere — <a href="https://findahelpline.com" target="_blank" rel="noreferrer" className="text-terracotta hover:underline">findahelpline.com</a></li>
                </ul>
            </Section>

            <Section title="using Pax">
                <p>· one account per person. don't share your login.</p>
                <p>· don't try to break, scrape, resell or otherwise abuse the service.</p>
                <p>· don't send content that harms others, violates their privacy, or is illegal in your country.</p>
                <p>· we may suspend accounts that repeatedly violate the above.</p>
            </Section>

            <Section title="content ownership">
                <p>you own the messages you send. Pax's replies are generated for you and you may use them for personal, non-commercial purposes. don't republish them as clinical advice.</p>
            </Section>

            <Section title="warranties & liability">
                <p>Pax is provided "as is" without warranty. to the maximum extent permitted by law, PaxMind AI is not liable for indirect, incidental or consequential damages arising from your use of the service.</p>
            </Section>

            <Section title="governing law">
                <p>these terms are governed by the laws of India. disputes will be resolved in the courts of the jurisdiction where PaxMind AI is registered.</p>
            </Section>
        </>
    );
}
